import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { API } from '../config';
import { useAuth } from '../contexts/AuthContext';

const POLL_MS = 30000; // poll every 30s

export const NotificationBell = ({ tone = 'dark' }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifs = async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user?.id]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!user) return null;

  const markRead = async (id, link) => {
    try { await axios.post(`${API}/notifications/${id}/read`, {}, { withCredentials: true }); } catch {}
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    if (link) navigate(link);
    setOpen(false);
  };

  const markAll = async () => {
    try { await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true }); } catch {}
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) fetchNotifs(); }}
        className="relative p-2 rounded-full"
        style={{ background: tone === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: tone === 'dark' ? '#F8FAF9' : '#080C0A' }}
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold px-1"
            style={{ background: '#EF4444', color: 'white' }}
            data-testid="notification-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-h-[480px] overflow-hidden rounded-2xl shadow-2xl z-50 flex flex-col"
          style={{ background: '#0c1f17', border: '1px solid rgba(212,175,55,0.25)' }}
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="font-medium" style={{ color: '#F8FAF9' }}>Notifikasi</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs flex items-center gap-1" style={{ color: '#D4AF37' }} data-testid="notification-mark-all-btn">
                <Check className="w-3 h-3" /> Tandai semua dibaca
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm" style={{ color: '#A3B8AC' }}>Belum ada notifikasi</p>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n.id, n.link)}
                className="w-full text-left px-4 py-3 transition-colors"
                style={{
                  background: n.read ? 'transparent' : 'rgba(212,175,55,0.06)',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
                data-testid={`notification-item-${n.id}`}
              >
                <p className="text-sm font-medium" style={{ color: n.read ? '#CFE0D5' : '#F8FAF9' }}>{n.title}</p>
                <p className="text-xs mt-0.5" style={{ color: '#A3B8AC' }}>{n.body}</p>
                <p className="text-[10px] mt-1" style={{ color: '#6B8079' }}>
                  {new Date(n.created_at).toLocaleString('id-ID')}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
